<template>
  <auth-layout pageClass="reset-password-page">
    <div class="row d-flex justify-content-center align-items-center">
      <div class="col-lg-4 col-md-6 col-sm-8">
        <ValidationObserver v-slot="{ handleSubmit }">
          <form class="label-control" @submit.prevent="handleSubmit(resetPassword)">
            <card>
              <div class="card-header" slot="header">
                <h3
                  class="card-title text-center"
                  style="color: #d9d9d9; font-weight: bold"
                >
                  Reset Password
                </h3>
              </div>
              <div v-if="!passwordReset">
                <p class="text-center" style="color: #d9d9d9; font-size: 14px;">
                  Enter your new password below.
                </p>
                <div class="login-fields-container">
                  <ValidationProvider
                    name="Password"
                    rules="required|min:8"
                    tag="div"
                    class="mb-3"
                    v-slot="{ errors }"
                  >
                    <label>
                      New Password
                      <div class="position-relative">
                        <el-input
                          v-model="password"
                          :type="passwordInputType"
                          class="small-select login-inputs-background"
                        ></el-input>
                        <i
                          :class="passwordVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                          @click="togglePasswordVisibility"
                          class="position-absolute"
                          style="
                            top: 50%;
                            right: 12px;
                            transform: translateY(-50%);
                            cursor: pointer;
                            color: #666;
                          "
                          aria-hidden="true"
                        ></i>
                      </div>
                    </label>
                    <div
                      v-if="errors.length"
                      class="text-danger"
                      style="font-size: 0.875em"
                    >
                      {{ errors[0] }}
                    </div>
                  </ValidationProvider>
                  <ValidationProvider
                    name="Confirm Password"
                    rules="required|password_match:@Password"
                    tag="div"
                    class="mb-3"
                    v-slot="{ errors }"
                  >
                    <label>
                      Confirm Password
                      <div class="position-relative">
                        <el-input
                          v-model="confirmPassword"
                          :type="confirmPasswordInputType"
                          class="small-select login-inputs-background"
                        ></el-input>
                        <i
                          :class="confirmPasswordVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                          @click="toggleConfirmPasswordVisibility"
                          class="position-absolute"
                          style="
                            top: 50%;
                            right: 12px;
                            transform: translateY(-50%);
                            cursor: pointer;
                            color: #666;
                          "
                          aria-hidden="true"
                        ></i>
                      </div>
                    </label>
                    <div
                      v-if="errors.length"
                      class="text-danger"
                      style="font-size: 0.875em"
                    >
                      {{ errors[0] }}
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div v-else>
                <div class="text-center" style="color: #d9d9d9;">
                  <i class="fa fa-check-circle fa-3x mb-3" style="color: #28a745;"></i>
                  <p>Your password has been reset successfully!</p>
                  <p>You can now login with your new password.</p>
                </div>
              </div>
              <div class="text-center" v-if="!passwordReset">
                <button
                  type="submit"
                  class="login-btn btn-fill btn-info btn-wd"
                  :disabled="loading"
                >
                  {{ loading ? 'Resetting...' : 'Reset Password' }}
                </button>
                <br />
                <div class="mt-3">
                  <router-link to="/login" class="back-to-login">
                    Back to Login
                  </router-link>
                </div>
              </div>
              <div class="text-center" v-else>
                <router-link to="/login" class="login-btn btn-fill btn-info btn-wd">
                  Go to Login
                </router-link>
              </div>
            </card>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </auth-layout>
</template>

<script>
import AuthLayout from "./AuthLayout.vue";
import { extend } from "vee-validate";
import { required, min } from "vee-validate/dist/rules";
import commonHandler from "src/serviceHandlers/commonHandler";

const apiService = new commonHandler();

extend("required", required);
extend("min", min);

extend("password_match", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "Passwords do not match",
});

export default {
  components: {
    AuthLayout,
  },
  data() {
    return {
      password: "",
      confirmPassword: "",
      passwordVisible: false,
      passwordInputType: "password",
      confirmPasswordVisible: false,
      confirmPasswordInputType: "password",
      passwordReset: false,
      loading: false,
      token: "",
    };
  },
  mounted() {
    this.token = this.$route.query.token;
    if (!this.token) {
      this.$notifyError("Invalid or missing reset token");
      this.$router.push("/forgot-password");
    }
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordInputType = this.passwordInputType === "password" ? "text" : "password";
      this.passwordVisible = !this.passwordVisible;
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordInputType = this.confirmPasswordInputType === "password" ? "text" : "password";
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
    },
    async resetPassword() {
      this.loading = true;
      this.$spinner.show();

      try {
        const response = await apiService.resetPassword({
          token: this.token,
          password: this.password,
          confirmPassword: this.confirmPassword,
        });

        if (response.success) {
          this.passwordReset = true;
          this.$notifySuccess("Password reset successfully!");

          setTimeout(() => {
            this.$router.push("/login");
          }, 3000);
        } else {
          this.$notifyError(response.message || "Failed to reset password. Please try again.");

          if (response.message && response.message.includes("expired")) {
            setTimeout(() => {
              this.$router.push("/forgot-password");
            }, 2000);
          }
        }
      } catch (error) {
        this.$notifyError("An error occurred. Please try again later.");
      } finally {
        this.loading = false;
        this.$spinner.hide();
      }
    },
  },
};
</script>

<style>
.card-header {
  background: none !important;
  height: 60px;
  font-family: "Playfair Display", serif;
}
</style>

<style scoped>
.card-title {
  font-family: "Playfair Display", serif;
}

.login-btn {
  background-color: rgba(37, 55, 65, 0.8);
  padding-bottom: 7px;
  padding-top: 5px;
  border-radius: 12px;
  border: 1px solid #253741;
  color: #d9d9d9 !important;
  font-family: "Nimbus Sans Light", sans-serif;
  font-weight: bold;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}

.login-btn:hover {
  background-color: rgba(37, 55, 65, 1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

label {
  color: #d9d9d9 !important;
  font-family: "Nimbus Sans Light", sans-serif;
  font-size: 14px !important;
  letter-spacing: 0.05rem !important;
}

.back-to-login {
  color: #d9d9d9;
  text-decoration: underline;
  font-size: 14px;
  transition: color 0.3s ease;
}

.back-to-login:hover {
  color: #fff;
}

.login-fields-container {
  margin-right: 20px;
}

.login-fields-container .small-select {
  width: 305px !important;
}

/* Mobile-specific adjustments */
@media (max-width: 576px) {
  .login-fields-container {
    padding: 0 8px;
    margin-right: 0;
  }

  .login-fields-container .small-select {
    width: 100% !important;
  }

  .card {
    margin: 0 !important;
  }
}
</style>